import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Plus, TreePalm, Check, X } from 'lucide-react';

export default function LeaveManagement() {
  const { user, isAdmin } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(12);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    leave_type: 'paid',
    start_date: '',
    end_date: '',
    reason: '',
  });
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      if (isAdmin) {
        const res = await api.get('/leaves');
        setLeaves(res.data);
      } else {
        const res = await api.get('/my/leaves');
        setLeaves(res.data.leaves);
        setLeaveBalance(res.data.paid_leave_balance);
      }
    } catch (error) {
      toast.error('Failed to fetch leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      await api.post('/my/leaves', formData);
      toast.success('Leave request submitted');
      setApplyOpen(false);
      setFormData({ leave_type: 'paid', start_date: '', end_date: '', reason: '' });
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleAction = async (status) => {
    try {
      await api.put(`/leaves/${selectedLeave.id}`, { status, admin_note: adminNote });
      toast.success(`Leave ${status}`);
      setActionOpen(false);
      setAdminNote('');
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process leave');
    }
  };

  const statusColor = (s) => {
    if (s === 'approved') return 'bg-green-100 text-green-800';
    if (s === 'rejected') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const filteredLeaves = filterStatus === 'all' ? leaves : leaves.filter(l => l.status === filterStatus);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">
            {isAdmin ? 'Leave Management' : 'My Leaves'}
          </h1>
          <p className="text-slate-500">
            {isAdmin ? 'Review and manage employee leave requests' : `Paid Leave Balance: ${leaveBalance} days`}
          </p>
        </div>
        <div className="flex gap-2">
          {!isAdmin && (
            <Button onClick={() => setApplyOpen(true)} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="apply-leave-btn">
              <Plus className="w-4 h-4 mr-2" /> Apply Leave
            </Button>
          )}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]" data-testid="leave-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="bg-white border shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="leaves-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {isAdmin && <TableHead className="font-semibold">Employee</TableHead>}
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">From</TableHead>
                <TableHead className="font-semibold">To</TableHead>
                <TableHead className="font-semibold">Days</TableHead>
                <TableHead className="font-semibold">Reason</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                {isAdmin && <TableHead className="font-semibold text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
              ) : filteredLeaves.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No leave requests found</TableCell></TableRow>
              ) : (
                filteredLeaves.map((leave) => (
                  <TableRow key={leave.id}>
                    {isAdmin && <TableCell className="font-medium">{leave.employee_name} <span className="text-xs text-slate-400">({leave.employee_code})</span></TableCell>}
                    <TableCell>
                      <Badge variant={leave.leave_type === 'paid' ? 'default' : 'secondary'}>
                        {leave.leave_type === 'paid' ? 'Paid' : 'Unpaid'}
                      </Badge>
                    </TableCell>
                    <TableCell>{leave.start_date}</TableCell>
                    <TableCell>{leave.end_date}</TableCell>
                    <TableCell className="font-bold">{leave.days}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{leave.reason}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(leave.status)}`}>
                        {leave.status}
                      </span>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {leave.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => { setSelectedLeave(leave); setActionOpen(true); }} data-testid={`action-leave-${leave.id}`}>
                            Review
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Apply Leave Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for Leave</DialogTitle>
            <DialogDescription>Submit a leave request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={formData.leave_type} onValueChange={(v) => setFormData(p => ({ ...p, leave_type: v }))}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid Leave ({leaveBalance} remaining)</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))} required data-testid="leave-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))} required data-testid="leave-end-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={formData.reason} onChange={(e) => setFormData(p => ({ ...p, reason: e.target.value }))} required placeholder="Reason for leave..." data-testid="leave-reason" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="submit-leave-btn">Submit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin Action Dialog */}
      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Leave Request</DialogTitle>
            <DialogDescription>
              {selectedLeave?.employee_name} - {selectedLeave?.leave_type} leave for {selectedLeave?.days} day(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p><strong>Dates:</strong> {selectedLeave?.start_date} to {selectedLeave?.end_date}</p>
              <p className="mt-1"><strong>Reason:</strong> {selectedLeave?.reason}</p>
            </div>
            <div className="space-y-2">
              <Label>Admin Note (optional)</Label>
              <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Optional note..." />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="destructive" onClick={() => handleAction('rejected')} data-testid="reject-leave-btn">
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button className="bg-[#138808] hover:bg-[#107307]" onClick={() => handleAction('approved')} data-testid="approve-leave-btn">
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
